import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { useDebounce } from 'react-use';
import uuid from 'uuid';
import isEqual from 'lodash/isEqual';
import {
  CompanyNameFilter,
  CompanyMode,
  TalentMatchingFilters,
} from 'talent_matching/types';
import { withLocalePrefix } from 'shared/utils/translation';

const t = withLocalePrefix('talent_matching.filter_row.experience');

const COMPANY_MODE_OPTIONS = [
  { label: t('must_have'), value: 'include' as CompanyMode },
  { label: t('does_not_have'), value: 'exclude' as CompanyMode },
];

export type ExperienceState = {
  companyRows: CompanyNameFilter[];
  minValue: string;
  maxValue: string;
  companyModeOptions: { label: string; value: CompanyMode }[];
  setMinValue: Dispatch<SetStateAction<string>>;
  setMaxValue: Dispatch<SetStateAction<string>>;
  handleCompanyNameChange: (id: string, value: string) => void;
  handleCompanyModeChange: (id: string, mode: CompanyMode) => void;
  handleAddCompanyRow: () => void;
  handleRemoveCompanyRow: (id: string) => void;
  clear: () => void;
  totalFiltersCount: number;
};

export const useExperienceState = (): ExperienceState => {
  const { setValue, watch } = useFormContext<TalentMatchingFilters>();
  const [minExperience, maxExperience, companyNameFilters] = watch([
    'minExperience',
    'maxExperience',
    'companyNameFilters',
  ]);
  const [minValue, setMinValue] = useState(minExperience || '');
  const [maxValue, setMaxValue] = useState(maxExperience || '');

  const emptyRows: CompanyNameFilter[] = useMemo(
    () => [{ id: uuid.v4(), mode: 'include' as CompanyMode, value: '' }],
    []
  );

  const filteredCompanyRows = useCallback(
    (rows: CompanyNameFilter[]) => {
      return rows?.length > 0
        ? rows.map((filter) => ({
            id: filter.id || uuid.v4(),
            mode: filter.mode as CompanyMode,
            value: filter.value ?? '',
          }))
        : emptyRows;
    },
    [emptyRows]
  );

  const initialCompanyRows: CompanyNameFilter[] =
    filteredCompanyRows(companyNameFilters);

  const [companyRows, setCompanyRows] =
    useState<CompanyNameFilter[]>(initialCompanyRows);

  useEffect(() => {
    setCompanyRows(filteredCompanyRows(companyNameFilters));
  }, [companyNameFilters, filteredCompanyRows]);

  const handleCompanyNameChange = (id: string, value: string) => {
    const nextRows = companyRows.map((row) =>
      row.id === id ? { ...row, value } : row
    );
    setCompanyRows(nextRows);
  };

  const handleCompanyModeChange = (id: string, mode: CompanyMode) => {
    const nextRows = companyRows.map((row) =>
      row.id === id ? { ...row, mode } : row
    );
    setCompanyRows(nextRows);
  };

  const handleAddCompanyRow = () => {
    const rows = [
      ...companyRows,
      { id: uuid.v4(), mode: 'include' as CompanyMode, value: '' },
    ];
    setCompanyRows(rows);
  };

  const handleRemoveCompanyRow = (id: string) => {
    const filteredRows = companyRows.filter((row) => row.id !== id);
    const rows = filteredRows.length > 0 ? filteredRows : emptyRows;
    setCompanyRows(rows);
  };

  const companyNameFiltersAppliedCount = companyRows.reduce((count, row) => {
    if (row.value.trim() !== '') {
      return count + 1;
    }
    return count;
  }, 0);

  const totalFiltersCount =
    companyNameFiltersAppliedCount + (minExperience || maxExperience ? 1 : 0);

  const clear = () => {
    setMinValue('');
    setMaxValue('');
    setCompanyRows(emptyRows);
    setValue('minExperience', null, { shouldDirty: true });
    setValue('maxExperience', null, { shouldDirty: true });
    setValue('companyNameFilters', emptyRows, {
      shouldDirty: true,
    });
  };

  useDebounce(
    () => {
      const normalizedMinValue = minValue === '' ? null : minValue;
      const normalizedMaxValue = maxValue === '' ? null : maxValue;

      if (minExperience !== normalizedMinValue) {
        setValue('minExperience', normalizedMinValue, { shouldDirty: true });
      }

      if (maxExperience !== normalizedMaxValue) {
        setValue('maxExperience', normalizedMaxValue, { shouldDirty: true });
      }
    },
    500,
    [minValue, maxValue, minExperience, maxExperience, setValue]
  );

  useDebounce(
    () => {
      const filters = companyRows.map(({ id, mode, value }) => ({
        id,
        mode,
        value,
      }));
      if (!isEqual(filters, companyNameFilters)) {
        setValue('companyNameFilters', filters, { shouldDirty: true });
      }
    },
    500,
    [companyRows, companyNameFilters, setValue]
  );

  useEffect(() => {
    setMinValue(minExperience || '');
    setMaxValue(maxExperience || '');
  }, [minExperience, maxExperience]);

  return {
    companyRows,
    minValue,
    maxValue,
    companyModeOptions: COMPANY_MODE_OPTIONS,
    setMinValue,
    setMaxValue,
    handleCompanyNameChange,
    handleCompanyModeChange,
    handleAddCompanyRow,
    handleRemoveCompanyRow,
    clear,
    totalFiltersCount,
  };
};
